"use client";

import {
  CTA_CLICKED_VALUES,
  useRoutePathname,
  useStatsigClient,
  type RegisteredStatsigEvent,
  type RegisteredEventName,
} from "@repo/statsig";
import React from "react";

import BrandedActionButton from "src/components/branded-action-button";
import { useFreeTrialExperiment } from "src/lib/use-free-trial-experiment";
import { useButtonPendingAction } from "src/lib/use-button-pending-action";

import { BillingToggle } from "./billing-toggle";
import { ProductTabs } from "./product-tabs";
import usePaywallProductTitles from "../hooks/use-paywall-product-titles";
import {
  usePaywallState,
  type BillingPeriod,
} from "../hooks/use-paywall-state";
import { ANALYTICS_EVENTS } from "../constants";

/**
 * The narrower product set this selector renders. `usePaywallState` is generic
 * so we keep the state type aligned with what ProductTabs supports, instead
 * of the wider `ProductType` union (which now includes the two-product
 * Owlet experiment variants).
 */
type SelectorProductType = "unlimitedRx" | "pediatricSupport";

export interface PaywallProductSelectorProps {
  /**
   * Handler when user clicks the CTA button.
   * @param product - Selected product key
   * @param billingPeriod - Selected billing period
   */
  onContinue: (
    product: SelectorProductType,
    billingPeriod: BillingPeriod
  ) => void;
  /**
   * Analytics event name for the continue action.
   * Allows different contexts to log different events.
   */
  analyticsEventName?: RegisteredEventName;
  /**
   * Additional metadata to include in analytics events.
   */
  analyticsMetadata?: Record<string, string>;
  /**
   * Whether to show loading state on the button.
   */
  isLoading?: boolean;
  /**
   * Custom class name for the container.
   */
  className?: string;
}

/**
 * Shared product selector component used by both the paywall page and subscription gate modal.
 * Contains billing toggle, product tabs, and CTA button.
 */
export function PaywallProductSelector({
  onContinue,
  analyticsEventName = ANALYTICS_EVENTS.paywallStartFreeTrial,
  analyticsMetadata = {},
  isLoading: externalLoading,
  className = "",
}: PaywallProductSelectorProps) {
  const { logEvent } = useStatsigClient();
  const locationData = useRoutePathname();
  const { hasFreeTrial } = useFreeTrialExperiment();

  // Get experiment values from Statsig
  const experimentValues = usePaywallProductTitles();

  // Internal loading state for navigation
  const { isPending: internalPending, startAction } = useButtonPendingAction();

  // Paywall state — narrow to the products this selector renders.
  const {
    selectedProduct,
    billingPeriod,
    setSelectedProduct,
    setBillingPeriod,
  } = usePaywallState<SelectorProductType>(
    "unlimitedRx",
    experimentValues.defaultBilling
  );

  const productTitle =
    selectedProduct === "unlimitedRx"
      ? experimentValues.RxProductTitle
      : experimentValues.PedsProductTitle;

  const handleContinue = () => {
    const statsigEvent: RegisteredStatsigEvent = {
      eventName: analyticsEventName,
      value: selectedProduct,
      metadata: {
        ...locationData,
        product: productTitle,
        billingCadence: billingPeriod,
        ...analyticsMetadata,
      },
    };
    logEvent(statsigEvent);

    startAction(() => onContinue(selectedProduct, billingPeriod));
  };

  const buttonText = hasFreeTrial ? "Start Free Trial" : "Get Now";
  const isLoading = externalLoading ?? internalPending;

  return (
    <div className={`flex flex-col gap-[16px] w-full ${className}`}>
      <BillingToggle
        billingPeriod={billingPeriod}
        onBillingPeriodChange={setBillingPeriod}
      />

      <ProductTabs
        selectedProduct={selectedProduct}
        onProductChange={setSelectedProduct}
        rxProductTitle={experimentValues.RxProductTitle}
        pedsProductTitle={experimentValues.PedsProductTitle}
        rxProductDescription={experimentValues.RxProductDescription}
        pedsProductDescription={experimentValues.PedsProductDescription}
        billingPeriod={billingPeriod}
        pedsMonthlyPrice={39}
        pedsYearlyPrice={399}
      />

      <BrandedActionButton
        text={buttonText}
        onPress={handleContinue}
        eventData={{
          action: CTA_CLICKED_VALUES.CONTINUE_CHECKOUT,
          product: productTitle,
          billingPeriod,
          "Free trial": hasFreeTrial.toString(),
          ...analyticsMetadata,
        }}
        className="!bg-teal-700 hover:!bg-teal-700 h-[44px] w-full rounded-[99px]"
        variant="primary"
        disabled={isLoading}
        loading={isLoading}
      />
    </div>
  );
}
